import React, { ReactNode, useState, useEffect } from 'react';
import { Box, CircularProgress, IconButton, Avatar, Menu, MenuItem, ListItemIcon, Divider, useMediaQuery, useTheme, Fade } from '@mui/material';
import apiClient from '../../services/api/config';
import { styled } from '@mui/material/styles';
import { DarkModeOutlined, LightModeOutlined, PersonOutline, SettingsOutlined, Analytics, AdminPanelSettings, Logout } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import NavigationRail from './NavigationRail';
import BottomHubBar from './BottomHubBar';
import HubTabs from './HubTabs';
import EnhancedAiraAI from '../ai/EnhancedAiraAI';
import RecognitionQuestionsPopup from '../recognition/RecognitionQuestionsPopup';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/ThemeContext';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';

const RAIL_WIDTH = 80;

const LayoutContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  overflowX: 'hidden',
  maxWidth: '100vw',
}));

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: RAIL_WIDTH,
  minHeight: '100vh',
  padding: '24px',
  backgroundColor: theme.palette.background.default,
  maxWidth: '100%',
  overflowX: 'hidden' as const,
  boxSizing: 'border-box' as const,
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
    padding: '12px',
    // Clear the fixed top bar (56px + its own safe-area inset) + 12px gap,
    // and keep the last row above the fixed bottom hub bar (58px) + home indicator.
    paddingTop: 'calc(68px + env(safe-area-inset-top))',
    paddingBottom: 'calc(70px + env(safe-area-inset-bottom))',
    maxWidth: '100vw',
  },
}));


interface MainLayoutProps {
  children: ReactNode;
}

function MainLayout({ children }: MainLayoutProps) {
  const { user, isLoading, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null);
  const { branding, mode, toggleMode } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = !!user && ['admin', 'company-super-admin', 'super-super-admin'].includes(user.role);
  const isSuperSuperAdmin = user?.role === 'super-super-admin';

  const goAccount = (path: string) => { setAccountAnchor(null); navigate(path); };

  // Close the account menu on any route change (in-page navigations bypass onClick).
  useEffect(() => {
    setAccountAnchor(null);
  }, [location.pathname, location.hash]);

  // Lightweight page-view instrumentation — feeds the Insights engagement
  // analytics (DAU/WAU/MAU, top sections). Fire-and-forget; never blocks nav.
  useEffect(() => {
    apiClient.post('/insights/track', { path: location.pathname }).catch(() => {});
  }, [location.pathname]);

  const sidebarBg = branding.sidebarColor || '#1A1D1F';

  if (isLoading || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <LayoutContainer>
      {isMobile ? (
        <>
          <Box sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            // Grow the bar under the iOS status bar so its controls clear it
            // in installed/standalone mode (inset is 0 on non-notched devices).
            height: 'calc(56px + env(safe-area-inset-top))',
            backgroundColor: sidebarBg,
            alignItems: 'center',
            padding: 'env(safe-area-inset-top) calc(16px + env(safe-area-inset-right)) 0 calc(16px + env(safe-area-inset-left))',
            zIndex: 1100,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}>
            <IconButton
              onClick={(e) => setAccountAnchor(e.currentTarget)}
              sx={{ p: 0.5, mr: 2 }}
              aria-label="Account menu"
            >
              <Avatar
                src={user.avatar ? resolveAssetUrl(user.avatar) : undefined}
                sx={{ width: 30, height: 30, fontSize: 14, bgcolor: branding.primaryColor || '#2D5BE3' }}
              >
                {(user.firstName?.[0] || user.email?.[0] || 'U').toUpperCase()}
              </Avatar>
            </IconButton>
            {branding.logoUrl ? (
              <Box
                component="img"
                src={resolveAssetUrl(branding.logoUrl)}
                alt={branding.companyName || 'Logo'}
                sx={{ width: 28, height: 28, objectFit: 'contain' }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/brand/logo-mark.svg`}
                alt="MoreMe AI"
                sx={{ width: 28, height: 28, filter: 'brightness(10)' }}
              />
            )}
            <IconButton
              onClick={toggleMode}
              sx={{ color: branding.sidebarTextColor || 'white', ml: 'auto' }}
              aria-label={mode === 'dark' ? 'Switch to day register' : 'Switch to night register'}
            >
              {mode === 'dark' ? <LightModeOutlined /> : <DarkModeOutlined />}
            </IconButton>
          </Box>
          <Menu
            anchorEl={accountAnchor}
            open={Boolean(accountAnchor)}
            onClose={() => setAccountAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            sx={{ zIndex: (t) => t.zIndex.tooltip + 10 }}
          >
            <MenuItem onClick={() => goAccount('/profile')}>
              <ListItemIcon><PersonOutline fontSize="small" /></ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => goAccount('/settings')}>
              <ListItemIcon><SettingsOutlined fontSize="small" /></ListItemIcon>
              Settings
            </MenuItem>
            {isAdmin && (
              <MenuItem onClick={() => goAccount('/insights')}>
                <ListItemIcon><Analytics fontSize="small" /></ListItemIcon>
                Insights
              </MenuItem>
            )}
            {isAdmin && (
              <MenuItem onClick={() => goAccount(isSuperSuperAdmin ? '/admin/super' : '/admin')}>
                <ListItemIcon><AdminPanelSettings fontSize="small" /></ListItemIcon>
                Admin Hub
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={() => { setAccountAnchor(null); logout(); }}>
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
          <BottomHubBar />
        </>
      ) : (
        <NavigationRail />
      )}
      <MainContent>
        <HubTabs />
        <Fade in timeout={280} key={location.pathname}>
          <div>{children}</div>
        </Fade>
      </MainContent>
      <EnhancedAiraAI />
      <RecognitionQuestionsPopup />
    </LayoutContainer>
  );
}

export default MainLayout;
